import React, { useState } from 'react';
import { Button, Card, Modal, Select, Table, Typography, message, Tag } from 'antd';
import { DeleteOutlined, EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { SiteType } from 'graphql/types/SiteType';
import { useWarehouseOptions } from '../../../hooks/useWarehouseOptions';
import { GET_AREAS_BY_WAREHOUSE_ID, GetAreasByWarehouseIdResponse } from '../../../graphql/queries/getAreasByWarehouseId';
import {
    LIST_SPLIT_ROUTE_CACHE,
    BATCH_DELETE_SPLIT_ROUTE_CACHE,
    ListSplitRouteCacheResponse,
    ListSplitRouteCacheVariables,
    BatchDeleteSplitRouteCacheResponse,
    BatchDeleteSplitRouteCacheVariables,
    SplitRouteCacheDetailItem,
} from './graphql/splitRouteCacheTools';

const { Title, Text } = Typography;

const MODE_LABELS: Record<string, string> = {
    map: 'Map',
    zipcode: 'Zipcode',
    stop: 'Stop',
    algo: 'Algorithm',
};

const SplitRouteCachePage: React.FC = () => {
    const { t } = useTranslation();
    const [warehouseId, setWarehouseId] = useState<string | undefined>();
    const [areaId, setAreaId] = useState<number | undefined>();
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailItem, setDetailItem] = useState<SplitRouteCacheDetailItem | null>(null);

    const { warehouseOptions, loading: warehousesLoading } = useWarehouseOptions(SiteType.WAREHOUSE);

    const { data: areasData, loading: areasLoading } = useQuery<GetAreasByWarehouseIdResponse>(
        GET_AREAS_BY_WAREHOUSE_ID,
        {
            variables: { warehouseId: Number(warehouseId) },
            skip: !warehouseId,
        }
    );
    const areaOptions = (areasData?.areaList ?? []).map(a => ({ value: Number(a.id), label: a.name }));

    const [runQuery, { loading: queryLoading, data: queryData }] = useLazyQuery<
        ListSplitRouteCacheResponse, ListSplitRouteCacheVariables
    >(LIST_SPLIT_ROUTE_CACHE, { fetchPolicy: 'network-only' });

    const [runBatchDelete, { loading: deleteLoading }] = useMutation<
        BatchDeleteSplitRouteCacheResponse, BatchDeleteSplitRouteCacheVariables
    >(BATCH_DELETE_SPLIT_ROUTE_CACHE);

    const cacheList = queryData?.listSplitRouteCache ?? [];

    const handleQuery = async () => {
        if (!warehouseId) {
            message.warning(t('supportTools.splitRouteCache.selectWarehouseFirst'));
            return;
        }
        await runQuery({ variables: { warehouseId: Number(warehouseId), areaId: areaId ?? null } });
    };

    const handleDeleteOne = (item: SplitRouteCacheDetailItem) => {
        Modal.confirm({
            title: t('supportTools.splitRouteCache.deleteConfirmTitle'),
            icon: <ExclamationCircleOutlined />,
            content: t('supportTools.splitRouteCache.deleteConfirmContent', { areaName: item.areaName }),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await runBatchDelete({ variables: { warehouseId: Number(warehouseId), areaIds: [item.areaId] } });
                    message.success(t('supportTools.splitRouteCache.deleteSuccess'));
                    handleQuery();
                } catch (e: any) {
                    message.error(e?.message ?? t('common.operationFailed'));
                }
            },
        });
    };

    const handleDeleteAll = () => {
        if (cacheList.length === 0) return;
        Modal.confirm({
            title: t('supportTools.splitRouteCache.deleteAllConfirmTitle'),
            icon: <ExclamationCircleOutlined />,
            content: t('supportTools.splitRouteCache.deleteAllConfirmContent', { count: cacheList.length }),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const ids = cacheList.map(c => c.areaId);
                    const { data } = await runBatchDelete({ variables: { warehouseId: Number(warehouseId), areaIds: ids } });
                    message.success(t('supportTools.splitRouteCache.deleteAllSuccess', { count: data?.batchDeleteSplitRouteCache ?? 0 }));
                    handleQuery();
                } catch (e: any) {
                    message.error(e?.message ?? t('common.operationFailed'));
                }
            },
        });
    };

    const parseSummary = (data: string | null) => {
        if (!data) return { mode: '-', segments: 0 };
        try {
            const parsed = JSON.parse(data);
            const mode = parsed.lastSplitMode ?? '-';
            let segments = 0;
            if (mode === 'algo') {
                segments = parsed.algo?.segments?.length ?? 0;
            } else if (Array.isArray(parsed[mode])) {
                segments = parsed[mode].length;
            }
            return { mode, segments };
        } catch {
            return { mode: '-', segments: 0 };
        }
    };

    const formatTime = (ts: number | null) => {
        if (!ts) return '-';
        const d = new Date(ts);
        const now = Date.now();
        const diffMin = Math.floor((now - ts) / 60000);
        if (diffMin < 1) return t('supportTools.splitRouteCache.justNow');
        if (diffMin < 60) return t('supportTools.splitRouteCache.minutesAgo', { count: diffMin });
        if (diffMin < 1440) return t('supportTools.splitRouteCache.hoursAgo', { count: Math.floor(diffMin / 60) });
        return d.toLocaleString();
    };

    const columns = [
        {
            title: 'Area ID',
            dataIndex: 'areaId',
            key: 'areaId',
            width: 100,
        },
        {
            title: t('supportTools.splitRouteCache.columnAreaName'),
            dataIndex: 'areaName',
            key: 'areaName',
            width: 150,
        },
        {
            title: t('supportTools.splitRouteCache.columnMode'),
            key: 'mode',
            width: 120,
            render: (_: any, record: SplitRouteCacheDetailItem) => {
                const { mode } = parseSummary(record.data);
                return <Tag color="purple">{MODE_LABELS[mode] ?? mode}</Tag>;
            },
        },
        {
            title: t('supportTools.splitRouteCache.columnSegments'),
            key: 'segments',
            width: 80,
            render: (_: any, record: SplitRouteCacheDetailItem) => parseSummary(record.data).segments,
        },
        {
            title: t('supportTools.splitRouteCache.columnSavedAt'),
            key: 'savedAt',
            width: 160,
            render: (_: any, record: SplitRouteCacheDetailItem) => formatTime(record.savedAt),
        },
        {
            title: t('supportTools.splitRouteCache.columnActions'),
            key: 'actions',
            width: 160,
            render: (_: any, record: SplitRouteCacheDetailItem) => (
                <div className="flex gap-2">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => { setDetailItem(record); setDetailVisible(true); }}
                    >
                        {t('supportTools.splitRouteCache.view')}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteOne(record)}
                    >
                        {t('supportTools.splitRouteCache.delete')}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div className="h-full w-full flex flex-col gap-4">
            <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-8 text-white shadow-sm">
                <Title level={2} style={{ margin: 0, color: 'white' }}>
                    {t('supportTools.splitRouteCache.title')}
                </Title>
                <div className="mt-2 text-white/90 text-base">
                    {t('supportTools.splitRouteCache.subtitle')}
                </div>
            </div>

            <Card className="rounded-2xl shadow-sm">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex flex-col gap-2 min-w-[240px]">
                            <Text className="text-base font-medium">{t('supportTools.splitRouteCache.warehouseLabel')}</Text>
                            <Select
                                showSearch
                                allowClear
                                placeholder={t('supportTools.splitRouteCache.warehousePlaceholder')}
                                className="w-full min-w-[220px]"
                                loading={warehousesLoading}
                                optionFilterProp="label"
                                options={warehouseOptions.map(w => ({ value: w.id, label: `${w.name} (${w.id})` }))}
                                value={warehouseId}
                                onChange={(v) => { setWarehouseId(v); setAreaId(undefined); }}
                            />
                        </div>
                        <div className="flex flex-col gap-2 min-w-[200px]">
                            <Text className="text-base font-medium">{t('supportTools.splitRouteCache.areaLabel')}</Text>
                            <Select
                                showSearch
                                allowClear
                                placeholder={t('supportTools.splitRouteCache.areaPlaceholder')}
                                className="w-full min-w-[180px]"
                                loading={areasLoading}
                                optionFilterProp="label"
                                options={areaOptions}
                                value={areaId}
                                onChange={setAreaId}
                                disabled={!warehouseId}
                            />
                        </div>
                        <Button type="primary" size="large" loading={queryLoading} onClick={handleQuery}>
                            {t('supportTools.splitRouteCache.queryButton')}
                        </Button>
                    </div>

                    {queryData && (
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <Text className="text-sm text-gray-500">
                                    {t('supportTools.splitRouteCache.resultCount', { count: cacheList.length })}
                                </Text>
                                {cacheList.length > 0 && (
                                    <Button danger size="small" loading={deleteLoading} onClick={handleDeleteAll}>
                                        {t('supportTools.splitRouteCache.deleteAll')}
                                    </Button>
                                )}
                            </div>
                            <Table
                                dataSource={cacheList}
                                columns={columns}
                                rowKey="areaId"
                                size="small"
                                pagination={false}
                                locale={{ emptyText: t('supportTools.splitRouteCache.noCache') }}
                            />
                        </div>
                    )}
                </div>
            </Card>

            <Modal
                title={t('supportTools.splitRouteCache.detailTitle')}
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                footer={null}
                width={720}
            >
                {detailItem && (() => {
                    const { mode, segments } = parseSummary(detailItem.data);
                    return (
                        <div className="flex flex-col gap-3">
                            <div className="flex gap-6 text-sm">
                                <span><Text strong>Area:</Text> {detailItem.areaName} ({detailItem.areaId})</span>
                                <span><Text strong>{t('supportTools.splitRouteCache.columnMode')}:</Text> <Tag color="purple">{MODE_LABELS[mode] ?? mode}</Tag></span>
                                <span><Text strong>{t('supportTools.splitRouteCache.columnSegments')}:</Text> {segments}</span>
                                <span><Text strong>{t('supportTools.splitRouteCache.columnSavedAt')}:</Text> {formatTime(detailItem.savedAt)}</span>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4 max-h-[500px] overflow-auto">
                                <pre className="text-xs whitespace-pre-wrap break-all m-0">
                                    {detailItem.data ? JSON.stringify(JSON.parse(detailItem.data), null, 2) : '-'}
                                </pre>
                            </div>
                        </div>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default SplitRouteCachePage;
